import React, { useState, useEffect, useRef } from 'react';
import { authFetch } from '../services/api';
import './ApplicationDependencies.css';

interface SkillsetMaster {
  skillset_id: string;
  skillset_name: string;
  skillset_description: string;
  created_at_formatted?: string;
  updated_at_formatted?: string;
}

const ResourceSkillsets: React.FC = () => {
  const [skillsets, setSkillsets] = useState<SkillsetMaster[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [filterSkillsetName, setFilterSkillsetName] = useState<string>('');
  const [editingSkillset, setEditingSkillset] = useState<SkillsetMaster | null>(null);
  const [viewMode, setViewMode] = useState(false);
  const [formData, setFormData] = useState({
    skillsetId: '',
    skillsetName: '',
    skillsetDescription: ''
  });

  useEffect(() => {
    fetchSkillsets();
  }, []);

  const fetchSkillsets = async () => {
    try {
      const response = await authFetch('/api/skillset-master');
      if (!response.ok) throw new Error('Failed to fetch');
      const result = await response.json();
      result.data.sort((a: SkillsetMaster, b: SkillsetMaster) => a.skillset_name.localeCompare(b.skillset_name));
      setSkillsets(result.data);
      setLoading(false);
    } catch (err: any) {
      setError(err.message || 'Failed to load skillset master records');
      setLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadFile(file);
    setShowUploadModal(true);
    
    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target?.result as string;
      const lines = text.split('\n').filter(line => line.trim());
      const headers = lines[0].split(',').map(h => h.trim());
      
      const data = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        const row: any = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        data.push(row);
      }
      
      setPreviewData(data);
    };
    
    reader.readAsText(file);
  };

  const handleBulkImport = async () => {
    try {
      // Transform CSV data to match API expected format (skillset_id is auto-generated)
      const transformedData = previewData.map(row => ({
        skillset_name: row['Skillset Name'] || row['skillset_name'] || '',
        skillset_description: row['Description'] || row['skillset_description'] || ''
      }));

      const response = await authFetch('/api/skillset-master/bulk-upload', {
        method: 'POST',
        body: JSON.stringify({ records: transformedData })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess(`Successfully imported ${data.data.successfulRecords} skillset records`);
        fetchSkillsets();
        
        if (data.data.errors > 0) {
          const errorMessage = data.data.errorDetails.join('\n');
          setError(`Import completed with ${data.data.errors} errors:\n${errorMessage}`);
        }
      } else {
        setError(data.message || 'Failed to import skillsets');
      }
      
      setShowUploadModal(false);
      setPreviewData([]);
      setUploadFile(null);
    } catch (err: any) {
      setError(err.message || 'Failed to import data');
    }

    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const template = `Skillset Name,Description
Cloud Computing,"AWS; Azure; GCP; Kubernetes; Docker; Terraform"
DevOps Engineering,"CI/CD; Jenkins; GitLab; Docker; Kubernetes; Infrastructure as Code"
Data Engineering,"SQL; Python; ETL; Data Warehousing; Big Data; Apache Spark"
Mobile Development,"iOS; Android; React Native; Flutter; Mobile UI/UX"
Web Development,"React; Angular; Vue; Node.js; HTML/CSS; JavaScript"
Quality Assurance,"Test Automation; Selenium; API Testing; Performance Testing"`;
    
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'skillset_master_template.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const downloadData = () => {
    if (skillsets.length === 0) {
      alert('No data to download');
      return;
    }

    // Match template format: Skillset Name,Description
    const headers = ['Skillset Name', 'Description'];
    const csvRows = [headers.join(',')];

    skillsets.forEach(record => {
      const row = [
        `"${(record.skillset_name || '').replace(/"/g, '""')}"`,
        `"${(record.skillset_description || '').replace(/"/g, '""')}"`
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `resource_skillsets_data_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleAddNew = () => {
    setEditingSkillset(null);
    setFormData({
      skillsetId: '',
      skillsetName: '',
      skillsetDescription: ''
    });
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingSkillset(null);
    setViewMode(false);
    setFormData({
      skillsetId: '',
      skillsetName: '',
      skillsetDescription: ''
    });
    setError('');
  };

  const handleView = (skillset: SkillsetMaster) => {
    setEditingSkillset(skillset);
    setFormData({
      skillsetId: skillset.skillset_id,
      skillsetName: skillset.skillset_name,
      skillsetDescription: skillset.skillset_description
    });
    setViewMode(true);
    setShowForm(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      // For new records, only send name and description (ID is auto-generated)
      // For updates, send name and description only (ID is in the URL)
      const payload = {
        skillset_name: formData.skillsetName,
        skillset_description: formData.skillsetDescription
      };

      const url = editingSkillset
        ? `/api/skillset-master/${editingSkillset.skillset_id}`
        : '/api/skillset-master';

      const method = editingSkillset ? 'PUT' : 'POST';

      const response = await authFetch(url, {
        method,
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (result.success) {
        setSuccess(result.message);
        // Clear form but keep modal open for adding more entries
        setFormData({
          skillsetId: '',
          skillsetName: '',
          skillsetDescription: ''
        });
        fetchSkillsets();
        // Clear any error messages
        setError('');
      } else {
        setError(result.message || 'Failed to save skillset');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save skillset');
    }
  };

  const handleEdit = (skillset: SkillsetMaster) => {
    setEditingSkillset(skillset);
    setFormData({
      skillsetId: skillset.skillset_id,
      skillsetName: skillset.skillset_name,
      skillsetDescription: skillset.skillset_description
    });
    setViewMode(false);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this skillset?')) {
      try {
        const response = await authFetch(`/api/skillset-master/${id}`, {
          method: 'DELETE'
        });
        if (!response.ok) throw new Error('Failed to delete');
        setSuccess('Skillset deleted successfully');
        fetchSkillsets();
      } catch (err: any) {
        setError(err.message || 'Failed to delete skillset');
      }
    }
  };

  if (loading) return <div className="md-loading">Loading skillset master records...</div>;

  const filteredSkillsets = skillsets.filter(skillset => {
    const nameMatch = filterSkillsetName === '' || 
      skillset.skillset_name.toLowerCase().includes(filterSkillsetName.toLowerCase());
    return nameMatch;
  });

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title">Resource Skillsets Management</h1>
            <p className="page-header__subtitle">
              Manage master skillsets and their descriptions for resource planning
            </p>
          </div>
          <div className="page-header__actions">
            <button
              className="btn btn-primary"
              onClick={handleAddNew}
              title="Create new skillset record"
            >
              ➕ Add Skillset
            </button>
            <button
              className="btn btn-outline-info"
              onClick={downloadTemplate}
              title="Download CSV template for bulk import"
            >
              📋 Download Template
            </button>
            <button
              className="btn btn-outline-primary"
              onClick={() => {
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
                fileInputRef.current?.click();
              }}
              title="Import skillsets from CSV"
            >
              📤 Upload File
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
            {skillsets.length > 0 && (
              <button
                className="btn btn-outline-success"
                onClick={downloadData}
                title="Download current data as CSV"
                style={{ border: '2px solid #28a745' }}
              >
                📥 Download Data
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="master-data-container">

      {/* Filter Section */}
      {skillsets.length > 0 && (
        <div className="md-card" style={{ marginBottom: '16px', padding: '16px' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px', 
            flexWrap: 'wrap',
            width: '100%',
            maxWidth: '100%'
          }}>
            <label style={{ fontSize: '16px', fontWeight: '500', minWidth: '120px' }}>Filter by Name:</label>
            <input
              type="text"
              value={filterSkillsetName}
              onChange={(e) => setFilterSkillsetName(e.target.value)}
              placeholder="Enter skillset name to filter..."
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '16px',
                minWidth: '200px',
                flex: '1',
                maxWidth: '300px'
              }}
            />
            {filterSkillsetName && (
              <button
                onClick={() => setFilterSkillsetName('')}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  whiteSpace: 'nowrap'
                }}
              >
                Clear Filter
              </button>
            )}
            <span style={{ 
              fontSize: '16px', 
              color: '#666',
              whiteSpace: 'nowrap'
            }}>
              Showing {filteredSkillsets.length} of {skillsets.length} skillsets
            </span>
          </div>
        </div>
      )}

      {/* Main Content Card */}
      <div className="md-card">
        {skillsets.length === 0 ? (
          <div className="md-empty-state" style={{ padding: '40px' }}>
            <div className="md-empty-icon">🎯</div>
            <h3>No Skillsets Found</h3>
            <p>Use the action cards above to add skillsets or upload data from a CSV file</p>
          </div>
        ) : (
          <div className="md-table-container">
            <table className="md-table" style={{ fontSize: '16px' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '8px', fontSize: '16px', whiteSpace: 'nowrap' }}>Skillset Name</th>
                  <th style={{ textAlign: 'left', padding: '8px', fontSize: '16px', whiteSpace: 'nowrap' }}>Description</th>
                  <th style={{ textAlign: 'center', padding: '8px', fontSize: '16px', whiteSpace: 'nowrap' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSkillsets.map(skillset => (
                  <tr key={skillset.skillset_id}>
                    <td style={{ textAlign: 'left', padding: '8px', fontSize: '16px', whiteSpace: 'nowrap' }}>
                      <span style={{ fontWeight: '500' }}>{skillset.skillset_name}</span>
                    </td>
                    <td style={{ textAlign: 'left', padding: '8px', fontSize: '16px', maxWidth: '300px' }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {skillset.skillset_description}
                      </div>
                    </td>
                    <td style={{ textAlign: 'center', padding: '8px', fontSize: '16px', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', alignItems: 'center' }}>
                        <button
                          className="action-btn view-btn"
                          onClick={() => handleView(skillset)}
                          title="View skillset details"
                          style={{
                            padding: '4px 6px',
                            backgroundColor: 'transparent',
                            color: '#1976d2',
                            border: '1px solid #1976d2',
                            borderRadius: '4px',
                            fontSize: '16px',
                            cursor: 'pointer',
                            minWidth: 'auto'
                          }}
                        >
                          👁️
                        </button>
                        <button
                          className="action-btn edit-btn"
                          onClick={() => handleEdit(skillset)}
                          title="Edit skillset"
                          style={{
                            padding: '4px 6px',
                            backgroundColor: 'transparent',
                            color: '#1976d2',
                            border: '1px solid #1976d2',
                            borderRadius: '4px',
                            fontSize: '16px',
                            cursor: 'pointer',
                            minWidth: 'auto'
                          }}
                        >
                          ✏️
                        </button>
                        <button
                          className="action-btn delete-btn"
                          onClick={() => handleDelete(skillset.skillset_id)}
                          title="Delete skillset"
                          style={{
                            padding: '4px 6px',
                            backgroundColor: 'transparent',
                            color: '#d32f2f',
                            border: '1px solid #d32f2f',
                            borderRadius: '4px',
                            fontSize: '16px',
                            cursor: 'pointer',
                            minWidth: 'auto'
                          }}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </div>

      {/* Skillset Form Modal */}
      {showForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            maxWidth: '600px',
            width: '95%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <div style={{
              padding: '24px',
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{margin: 0, fontSize: '24px', fontWeight: '500', color: '#212121'}}>
                {viewMode ? 'View Skillset' : (editingSkillset ? 'Edit Skillset' : 'Add New Skillset')}
              </h2>
              <button 
                onClick={handleCloseForm}
                style={{
                  width: '32px',
                  height: '32px',
                  border: 'none',
                  background: 'transparent',
                  fontSize: '16px',
                  cursor: 'pointer',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#757575'
                }}
              >
                ×
              </button>
            </div>
            <div style={{padding: '24px'}}>
              <form onSubmit={handleSubmit}>
                {error && (
                  <div className="md-error" style={{ marginBottom: '16px' }}>
                    {error}
                  </div>
                )}
                
                <div className="md-form-section">
                  <h3>Basic Information</h3>
                  <div className="md-form-row">
                    {(editingSkillset || viewMode) && (
                      <div className="md-form-group">
                        <label>Skillset ID</label>
                        <input
                          type="text"
                          className="md-input"
                          name="skillsetId"
                          value={formData.skillsetId}
                          disabled
                          style={{
                            backgroundColor: '#f5f5f5',
                            cursor: 'not-allowed'
                          }}
                        />
                      </div>
                    )}
                    <div className="md-form-group">
                      <label>Skillset Name *</label>
                      <input
                        type="text"
                        className="md-input"
                        name="skillsetName"
                        value={formData.skillsetName}
                        onChange={handleInputChange}
                        placeholder="e.g., Cloud Computing, Web Development"
                        required
                        maxLength={100}
                        disabled={viewMode}
                        style={{
                          backgroundColor: viewMode ? '#f5f5f5' : 'white',
                          cursor: viewMode ? 'not-allowed' : 'text'
                        }}
                      />
                    </div>
                  </div>
                </div>
                  
                <div className="md-form-section">
                  <h3>Skillset Details</h3>
                  <div className="md-form-group">
                    <label>Description *</label>
                    <textarea
                      className="md-input"
                      name="skillsetDescription"
                      value={formData.skillsetDescription}
                      onChange={handleInputChange}
                      placeholder="Detailed description of skills and technologies included in this skillset (e.g., AWS; Azure; GCP; Kubernetes; Docker; Terraform)"
                      required
                      maxLength={300}
                      rows={4}
                      disabled={viewMode}
                      style={{
                        backgroundColor: viewMode ? '#f5f5f5' : 'white',
                        cursor: viewMode ? 'not-allowed' : 'text'
                      }}
                    />
                    <div style={{ fontSize: '16px', color: '#666', marginTop: '4px' }}>
                      {formData.skillsetDescription.length}/300 characters - List specific technologies, tools, and competencies
                    </div>
                  </div>
                </div>
                
                <div style={{display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '20px', borderTop: '1px solid #e0e0e0'}}>
                  <button
                    type="button"
                    onClick={handleCloseForm}
                    style={{padding: '12px 24px', fontSize: '16px', fontWeight: '500', border: '1px solid #1976d2', borderRadius: '4px', cursor: 'pointer', backgroundColor: 'transparent', color: '#1976d2'}}
                  >
                    {viewMode ? 'Close' : 'Cancel'}
                  </button>
                  {!viewMode && (
                    <button
                      type="submit"
                      style={{padding: '12px 24px', fontSize: '16px', fontWeight: '500', border: 'none', borderRadius: '4px', cursor: 'pointer', backgroundColor: '#1976d2', color: 'white'}}
                    >
                      {editingSkillset ? 'Update Skillset' : 'Create Skillset'}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            maxWidth: '800px',
            width: '95%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <div style={{
              padding: '24px',
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{margin: 0, fontSize: '16px', fontWeight: '400'}}>Preview Import Data</h2>
              <button 
                onClick={() => setShowUploadModal(false)}
                style={{
                  width: '32px',
                  height: '32px',
                  border: 'none',
                  background: 'transparent',
                  fontSize: '16px',
                  cursor: 'pointer',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>
            <div style={{padding: '24px'}}>
              {previewData.length > 0 ? (
                <div>
                  <p>Found {previewData.length} records. Preview:</p>
                  <div style={{ maxHeight: '300px', overflow: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
                    <table style={{ width: '100%', fontSize: '16px' }}>
                      <thead style={{ backgroundColor: '#f5f5f5' }}>
                        <tr>
                          {Object.keys(previewData[0] || {}).map(key => (
                            <th key={key} style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                              {key}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.slice(0, 5).map((row, index) => (
                          <tr key={index}>
                            {Object.values(row).map((value: any, cellIndex) => (
                              <td key={cellIndex} style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                                {value}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {previewData.length > 5 && (
                    <p style={{ fontSize: '16px', color: '#666' }}>
                      ... and {previewData.length - 5} more records
                    </p>
                  )}
                </div>
              ) : (
                <p>No data found in the uploaded file.</p>
              )}
              
              <div style={{display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px'}}>
                <button
                  onClick={() => setShowUploadModal(false)}
                  style={{padding: '12px 24px', fontSize: '16px', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer', backgroundColor: 'white'}}
                >
                  Cancel
                </button>
                {previewData.length > 0 && (
                  <button
                    onClick={handleBulkImport}
                    style={{padding: '12px 24px', fontSize: '16px', border: 'none', borderRadius: '4px', cursor: 'pointer', backgroundColor: '#1976d2', color: 'white'}}
                  >
                    Import {previewData.length} Records
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Success/Error Messages */}
      {success && (
        <div className="md-toast md-toast--success">
          {success}
          <button onClick={() => setSuccess('')}>×</button>
        </div>
      )}
      {error && (
        <div className="md-toast md-toast--error">
          {error}
          <button onClick={() => setError('')}>×</button>
        </div>
      )}
    </div>
  );
};

export default ResourceSkillsets;